import { actions, ActionTypes, assign, createMachine, DoneInvokeEvent, EventObject, send, sendParent } from "xstate"
import { Materialized } from "@silversea-ssc/functional-support"
import { PathReporter } from "io-ts/PathReporter"
import { Errors } from "io-ts"
import * as TE from "fp-ts/TaskEither"
import * as E from "fp-ts/Either"
import * as F from "fp-ts/function"
import * as A from "fp-ts/Array"
import * as T from "fp-ts/Task"
import { assertEvent } from "../xstate-helpers"
import {
    DataLoadContext,
    DataLoadEvents,
    DataLoadStateMachine,
    httpDataLoadTypes,
    LoadFatalError,
} from "./http-data-load-machine.types"

type NotifyParent<ASuccess, TParentEvent extends EventObject> = (
    ctx: DataLoadContext<ASuccess>,
    ev: DataLoadEvents<ASuccess>,
) => TParentEvent | undefined

type DataLoadConfig<ESuccess, ASuccess, TParentEvent extends EventObject> = {
    id: string
    Success: Materialized<ESuccess, ASuccess>
    notifyParent: NotifyParent<ASuccess, TParentEvent>
}

export const httpDataLoadMachine = <ESuccess, ASuccess, TParentEvent extends EventObject>(
    config: DataLoadConfig<ESuccess, ASuccess, TParentEvent>,
): DataLoadStateMachine<ASuccess> => {
    const { id, Success, notifyParent } = config
    const types = httpDataLoadTypes(Success)
    type Types = typeof types
    type Context = Types["Context"]
    type Events = Types["Events"]
    type TypeState = Types["TypeState"]

    return createMachine<Context, Events, TypeState>(
        {
            id: `http-data-load-${id}`,
            initial: "idle",
            predictableActionArguments: true,
            context: { id },
            states: {
                idle: {
                    on: { LOAD: { target: "loading", actions: [] } },
                },
                loading: {
                    entry: "clearError",
                    invoke: {
                        src: "sendRequest",
                        onDone: { actions: "onDone" },
                        onError: { actions: "errorShouldNeverHappen" },
                    },
                    on: {
                        LOAD: "loading",
                        LOAD_COMPLETED: { target: "loaded", actions: ["setResponse", "notifyParent"] },
                        LOAD_FATAL_ERROR: { target: "error", actions: ["setFatalError", "notifyParent"] },
                    },
                },
                loaded: {
                    on: { LOAD: "loading" },
                },
                error: {
                    entry: "logError",
                    on: { LOAD: "loading" },
                },
            },
        },
        {
            actions: {
                onDone: send<Context, Events>((context, event) => {
                    if (!event.type.startsWith(ActionTypes.DoneInvoke)) {
                        throw new Error(
                            `Invariant violation: Expected event "${ActionTypes.DoneInvoke}" but instead got "${event.type}"`,
                        )
                    }
                    const ev = event as DoneInvokeEvent<FetchResults>
                    return F.pipe(
                        ev.data.right,
                        traverseAE(Success.strictType.decode),
                        E.foldW(parseErrorEvent, (successes): Types["LoadCompleted"] => ({
                            type: "LOAD_COMPLETED",
                            successes,
                            failures: ev.data.left,
                        })),
                    )
                }),
                errorShouldNeverHappen: send<Context, Events, Events>((_, event) => {
                    if (!event.type.startsWith(ActionTypes.ErrorPlatform)) {
                        throw new Error(
                            `Invariant violation: Expected event "${ActionTypes.ErrorPlatform}" but instead got "${event.type}"`,
                        )
                    }
                    const ev = event as DoneInvokeEvent<unknown>
                    return {
                        type: "LOAD_FATAL_ERROR",
                        message: `This should never happen: ${ev.data}`,
                    }
                }),
                setResponse: assign<Context, Events>((_, ev) => {
                    assertEvent(ev, "LOAD_COMPLETED")
                    return { successes: ev.successes, failures: ev.failures }
                }),
                notifyParent: actions.pure<Context, Events>((ctx, ev) => {
                    const parentEvent = notifyParent(ctx, ev)
                    return parentEvent ? sendParent(parentEvent) : undefined
                }),
                setFatalError: assign<Context, Events>({
                    error: (_, ev) => {
                        assertEvent(ev, "LOAD_FATAL_ERROR")
                        return { type: "FATAL", message: ev.message }
                    },
                }),
                clearError: assign<Context, Events>({
                    error: undefined,
                }),
                logMe: actions.log<Context, Events>((ctx, ev) => ({ type: ev.type, ctx, ev })),
                logError: actions.log<Context, Events>((ctx, ev) => ({ type: "ERROR", ctx, ev })),
            },
            services: {
                sendRequest: (_context, event) => {
                    assertEvent(event, "LOAD")
                    return tryFetchAllJson(event.request)
                },
            },
        },
    )
}

const parseErrorEvent = (error: Errors): LoadFatalError => ({
    type: "LOAD_FATAL_ERROR",
    message: PathReporter.report(E.left(error)).join("\n"),
})

type FetchResults = Awaited<ReturnType<typeof tryFetchAllJson>>
const tryFetchAllJson = (requests: Request[]) => F.pipe(requests, traverseAT(fetchJson), T.map(A.separate))()

const fetchJson = F.flow(
    TE.tryCatchK((r: Request) => fetch(r), E.toError),
    TE.chainW(TE.fromPredicate((x) => x.ok, F.identity)),
    TE.chainW(TE.tryCatchK((x): Promise<unknown> => x.json(), E.toError)),
)

const traverseAE = A.traverse(E.Applicative)
const traverseAT = A.traverse(T.ApplicativePar)
